import { Flex, Text, usePrefersReducedMotion } from '@chakra-ui/react'
import { UserRoundMinus, UserRoundPlus } from 'lucide-react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFeatureGate } from '../../hooks/useFeatureGate'
import { useRole } from '../../hooks/auth/useRole'

const LAST_HIRING_ROUTE = 'aboutwrk:people-flow:hiring-route'
const LAST_DEPARTURE_ROUTE = 'aboutwrk:people-flow:departure-route'

const safeStoredRoute = (key: string, prefix: string, fallback: string) => {
  if (typeof window === 'undefined') return fallback
  const stored = window.sessionStorage.getItem(key)
  if (stored && (stored === prefix || stored.startsWith(`${prefix}/`))) return stored
  return fallback
}

/**
 * A human-readable switch between the two halves of the employee lifecycle.
 * Existing inflow/outflow URLs remain canonical so bookmarks, emails, and API
 * cache keys keep working; this component only unifies how people navigate them.
 */
const PeopleFlowSwitcher: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const reduceMotion = usePrefersReducedMotion()
  const { isOrgAdmin, isSuperAdmin } = useRole()
  const { hasFeature } = useFeatureGate()

  const isHiring = location.pathname.startsWith('/user/inflow')
  const isDeparture = location.pathname.startsWith('/user/outflow')
  const canSeeHiring = isSuperAdmin || hasFeature('inflow') || isHiring
  const canSeeDepartures =
    (isOrgAdmin || isSuperAdmin) && (isSuperAdmin || hasFeature('outflow') || isDeparture)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const route = `${location.pathname}${location.search}${location.hash}`
    if (isHiring) window.sessionStorage.setItem(LAST_HIRING_ROUTE, route)
    if (isDeparture) window.sessionStorage.setItem(LAST_DEPARTURE_ROUTE, route)
  }, [isDeparture, isHiring, location.hash, location.pathname, location.search])

  const choices = [
    ...(canSeeHiring
      ? [{
          id: 'hiring',
          label: t('peopleFlow.hiring.title', 'Hiring & onboarding'),
          helper: t('peopleFlow.hiring.helper', 'From candidate to first day'),
          icon: UserRoundPlus,
          active: isHiring,
          path: safeStoredRoute(LAST_HIRING_ROUTE, '/user/inflow', '/user/inflow/candidates'),
        }]
      : []),
    ...(canSeeDepartures
      ? [{
          id: 'departures',
          label: t('peopleFlow.departures.title', 'Departures'),
          helper: t('peopleFlow.departures.helper', 'From notice to final handover'),
          icon: UserRoundMinus,
          active: isDeparture,
          path: safeStoredRoute(LAST_DEPARTURE_ROUTE, '/user/outflow', '/user/outflow/offboarding'),
        }]
      : []),
  ]

  if (choices.length < 2) return null

  return (
    <Flex
      as="nav"
      aria-label={t('peopleFlow.switcherLabel', 'People flow navigation')}
      gap="2px"
      p="3px"
      mb="12px"
      w={{ base: '100%', md: 'fit-content' }}
      maxW="100%"
      bg="white"
      border="1px solid"
      borderColor="solid.50"
      borderRadius="11px"
      boxShadow="0 1px 2px rgba(24, 24, 35, 0.03)"
    >
      {choices.map((choice) => {
        const ChoiceIcon = choice.icon
        return (
          <Flex
            key={choice.id}
            as="button"
            type="button"
            aria-current={choice.active ? 'page' : undefined}
            onClick={() => navigate(choice.path)}
            align="center"
            gap="7px"
            minW={{ base: 0, md: '176px' }}
            flex="1"
            px={{ base: '10px', md: '13px' }}
            py="8px"
            textAlign="left"
            borderRadius="8px"
            color={choice.active ? 'primary.100' : 'solid.600'}
            bg={choice.active ? 'primary.100' : 'transparent'}
            border="0"
            boxShadow={choice.active ? '0 2px 7px rgba(93, 94, 203, 0.18)' : 'none'}
            transition={reduceMotion ? undefined : 'background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease'}
            _hover={{ bg: choice.active ? 'primary.100' : 'solid.25', color: choice.active ? 'white' : 'solid.900' }}
            _focusVisible={{ outline: '3px solid', outlineColor: 'primary.50', outlineOffset: '2px' }}
          >
            <Flex
              boxSize="26px"
              flexShrink={0}
              align="center"
              justify="center"
              borderRadius="7px"
              bg={choice.active ? 'whiteAlpha.200' : 'solid.25'}
            >
              <ChoiceIcon size={14} strokeWidth={2.1} />
            </Flex>
              <Text fontSize={{ base: '12px', md: '13px' }} fontWeight="700" color={choice.active ? 'white' : 'solid.700'} whiteSpace="nowrap">
                {choice.label}
              </Text>
          </Flex>
        )
      })}
    </Flex>
  )
}

export default PeopleFlowSwitcher
